// Inbox: Patienten-Anfragen (Krankmeldung, Folgerezept, Überweisung).
// Bietet Status- und Typ-Filter, Detail-Ansicht und Status-Aktionen.

import { useEffect, useState } from 'react';
import {
  deleteAnfrage,
  getInbox,
  updateAnfrage,
  type AnfrageTyp,
  type InboxStats,
  type KrankmeldungAnfrage,
} from '../api';

const STATUS_LABEL: Record<KrankmeldungAnfrage['status'], string> = {
  eingegangen: 'Eingegangen',
  bestaetigt: 'Bestätigt',
  abgelehnt: 'Abgelehnt',
  erledigt: 'Erledigt',
};

const TYP_LABEL: Record<AnfrageTyp, string> = {
  krankmeldung: 'Krankmeldung',
  folgerezept: 'Folgerezept',
  ueberweisung: 'Überweisung',
};

const TYP_COLOR: Record<AnfrageTyp, string> = {
  krankmeldung: '#E99754',
  folgerezept: '#5BAA98',
  ueberweisung: '#7B68EE',
};

const ROLLE_LABEL: Record<string, string> = {
  selbst: 'Patient/in selbst',
  partner: 'Partner/Partnerin',
  kind: 'Elternteil für Kind',
  mutter: 'Tochter/Sohn für Mutter',
  vater: 'Tochter/Sohn für Vater',
  oma: 'Enkel/in für Großmutter',
  opa: 'Enkel/in für Großvater',
  andere: 'Bevollmächtigte/r',
};

/**
 * Inbox-Page. Wird in drei Varianten in der Sidebar verwendet:
 *   - fixedTyp='krankmeldung' → "Krankmeldungen"
 *   - fixedTyp='folgerezept'  → "Folgerezepte"
 *   - fixedTyp='ueberweisung' → "Überweisungen"
 * Wenn fixedTyp gesetzt ist, sind die Typ-Pills versteckt und der Titel
 * passt sich an. Status-Pills (Offen/Bestätigt/...) bleiben in jedem Fall.
 */
export default function Inbox({ fixedTyp }: { fixedTyp?: AnfrageTyp } = {}) {
  const [anfragen, setAnfragen] = useState<KrankmeldungAnfrage[]>([]);
  const [stats, setStats] = useState<InboxStats | null>(null);
  const [selected, setSelected] = useState<KrankmeldungAnfrage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<'alle' | KrankmeldungAnfrage['status']>('eingegangen');
  const [typFilter, setTypFilter] = useState<'alle' | AnfrageTyp>(fixedTyp ?? 'alle');

  const TITEL: Record<AnfrageTyp, { title: string; sub: string }> = {
    krankmeldung: {
      title: 'Krankmeldungen',
      sub: 'Krankmeldungs-Anforderungen Ihrer Patienten — bestätigen oder ablehnen.',
    },
    folgerezept: {
      title: 'Folgerezepte',
      sub: 'Anträge auf Folgerezept für bereits verordnete Medikamente.',
    },
    ueberweisung: {
      title: 'Überweisungen',
      sub: 'Anträge auf Überweisung zu einem Facharzt.',
    },
  };
  const pageInfo = fixedTyp ? TITEL[fixedTyp] : {
    title: 'Alle Anfragen',
    sub: 'Patientenanfragen für Krankmeldung, Folgerezept und Überweisung.',
  };

  const reload = async () => {
    try {
      const r = await getInbox();
      setAnfragen(r.anfragen);
      setStats(r.stats);
      if (selected) {
        const updated = r.anfragen.find((a) => a.id === selected.id);
        if (updated) setSelected(updated);
      }
    } catch (e: any) {
      setErr(e.message);
    }
  };
  useEffect(() => { reload(); }, []);

  const gefiltert = anfragen.filter((a) => {
    if (filter !== 'alle' && a.status !== filter) return false;
    const typ = (a.typ ?? 'krankmeldung') as AnfrageTyp;
    if (typFilter !== 'alle' && typ !== typFilter) return false;
    return true;
  });

  // Typ-Zähler für die Filter-Pills
  const typCounts: Record<'alle' | AnfrageTyp, number> = {
    alle: anfragen.length,
    krankmeldung: 0,
    folgerezept: 0,
    ueberweisung: 0,
  };
  for (const a of anfragen) {
    const t = (a.typ ?? 'krankmeldung') as AnfrageTyp;
    typCounts[t]++;
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
        <div>
          <h1 className="page-title">{pageInfo.title}</h1>
          <p className="page-sub">{pageInfo.sub}</p>
        </div>
        <button className="btn btn-ghost" onClick={reload}>↻ Neu laden</button>
      </div>

      {/* Typ-Filter nur bei Sammel-Ansicht — bei fixedTyp irrelevant */}
      {!fixedTyp && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <TypChip label="Alle Typen" count={typCounts.alle} active={typFilter === 'alle'} onClick={() => setTypFilter('alle')} />
          <TypChip label="Krankmeldung" count={typCounts.krankmeldung} color={TYP_COLOR.krankmeldung} active={typFilter === 'krankmeldung'} onClick={() => setTypFilter('krankmeldung')} />
          <TypChip label="Folgerezept" count={typCounts.folgerezept} color={TYP_COLOR.folgerezept} active={typFilter === 'folgerezept'} onClick={() => setTypFilter('folgerezept')} />
          <TypChip label="Überweisung" count={typCounts.ueberweisung} color={TYP_COLOR.ueberweisung} active={typFilter === 'ueberweisung'} onClick={() => setTypFilter('ueberweisung')} />
        </div>
      )}

      {err && <div className="card" style={{ color: '#D9534F', marginBottom: 12 }}>Fehler: {err}</div>}

      {/* Stat-Cards — bei fixedTyp typ-bezogene Counts, sonst Gesamt */}
      {stats && (() => {
        const scope = fixedTyp
          ? anfragen.filter((a) => (a.typ ?? 'krankmeldung') === fixedTyp)
          : anfragen;
        const s = {
          total: scope.length,
          eingegangen: scope.filter((a) => a.status === 'eingegangen').length,
          bestaetigt: scope.filter((a) => a.status === 'bestaetigt').length,
          abgelehnt: scope.filter((a) => a.status === 'abgelehnt').length,
          erledigt: scope.filter((a) => a.status === 'erledigt').length,
        };
        return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
          <StatCard label="Offen" value={s.eingegangen} accent="#E99754" active={filter === 'eingegangen'} onClick={() => setFilter('eingegangen')} />
          <StatCard label="Bestätigt" value={s.bestaetigt} accent="#7BB592" active={filter === 'bestaetigt'} onClick={() => setFilter('bestaetigt')} />
          <StatCard label="Abgelehnt" value={s.abgelehnt} accent="#D9534F" active={filter === 'abgelehnt'} onClick={() => setFilter('abgelehnt')} />
          <StatCard label="Erledigt" value={s.erledigt} accent="#5F6B68" active={filter === 'erledigt'} onClick={() => setFilter('erledigt')} />
          <StatCard label="Insgesamt" value={s.total} active={filter === 'alle'} onClick={() => setFilter('alle')} />
        </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 16 }}>
        {/* Liste */}
        <div className="card" style={{ padding: 0, maxHeight: '70vh', overflow: 'auto' }}>
          {gefiltert.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#5F6B68' }}>
              Keine {filter === 'alle' ? 'Anforderungen' : STATUS_LABEL[filter].toLowerCase() + 'en'}.
            </div>
          ) : (
            gefiltert.map((a) => {
              const typ = (a.typ ?? 'krankmeldung') as AnfrageTyp;
              const preview =
                typ === 'folgerezept'
                  ? a.folgerezept?.medikament ?? ''
                  : typ === 'ueberweisung'
                  ? `→ ${a.ueberweisung?.fachrichtung ?? ''}`
                  : a.beschwerde;
              return (
                <div
                  key={a.id}
                  className={'inbox-row' + (selected?.id === a.id ? ' selected' : '')}
                  onClick={() => setSelected(a)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                        background: TYP_COLOR[typ], color: '#fff',
                        padding: '2px 6px', borderRadius: 3,
                      }}>
                        {TYP_LABEL[typ]}
                      </span>
                      {typ === 'ueberweisung' && a.ueberweisung?.dringlichkeit === 'eilig' && (
                        <span style={{ fontSize: 10, color: '#D9534F', fontWeight: 700 }}>EILIG</span>
                      )}
                    </div>
                    <div style={{ fontWeight: 600 }}>
                      {a.patient.vorname} {a.patient.nachname}
                    </div>
                    <div style={{ fontSize: 12, color: '#5F6B68', marginTop: 2 }}>
                      {preview.slice(0, 60)}{preview.length > 60 ? '…' : ''}
                    </div>
                    <div style={{ fontSize: 11, color: '#5F6B68', marginTop: 4 }}>
                      {new Date(a.empfangen_am).toLocaleString('de-DE')}
                    </div>
                  </div>
                  <span className={`status-badge status-${a.status}`}>{STATUS_LABEL[a.status]}</span>
                </div>
              );
            })
          )}
        </div>

        {/* Detail */}
        <div>
          {selected ? (
            <AnfrageDetail anfrage={selected} onChange={reload} />
          ) : (
            <div className="card" style={{ textAlign: 'center', color: '#5F6B68', padding: 40 }}>
              Wähle eine Anforderung aus der Liste.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function TypChip({
  label, count, color = '#5F6B68', active, onClick,
}: { label: string; count: number; color?: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 999,
        border: `1px solid ${active ? color : '#DDE9E8'}`,
        background: active ? color : '#fff',
        color: active ? '#fff' : '#1D1D1B',
        fontSize: 12,
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      {label} {count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
    </button>
  );
}

function StatCard({ label, value, accent = '#00A099', active, onClick }: { label: string; value: number; accent?: string; active?: boolean; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="card stat-card"
      style={{
        textAlign: 'left', cursor: 'pointer', font: 'inherit',
        borderLeftColor: accent,
        outline: active ? `2px solid ${accent}` : 'none',
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </button>
  );
}

function AnfrageDetail({ anfrage, onChange }: { anfrage: KrankmeldungAnfrage; onChange: () => void }) {
  const [notiz, setNotiz] = useState(anfrage.praxis_notiz ?? '');
  const [saving, setSaving] = useState(false);
  // Modal-State für Status-Wechsel mit Begleittext.
  // Beim Klick auf Bestätigen/Ablehnen/Erledigt öffnet sich der Dialog,
  // damit die Praxis eine Nachricht an den Patienten mitschicken kann.
  // Die Nachricht landet in der App (Chat-Thread, Anfragen-Liste) und in der Mail.
  const [statusModal, setStatusModal] = useState<null | {
    status: 'bestaetigt' | 'abgelehnt' | 'erledigt' | 'eingegangen';
    note: string;
  }>(null);

  const openStatusModal = (status: 'bestaetigt' | 'abgelehnt' | 'erledigt' | 'eingegangen') => {
    setStatusModal({ status, note: notiz });
  };

  const confirmStatus = async () => {
    if (!statusModal) return;
    setSaving(true);
    try {
      const noteTrim = statusModal.note.trim();
      await updateAnfrage(anfrage.id, {
        status: statusModal.status,
        praxis_notiz: noteTrim.length > 0 ? noteTrim : undefined,
      });
      // Lokale Notiz für die interne Anzeige aktualisieren
      setNotiz(noteTrim);
      setStatusModal(null);
      onChange();
    } catch (e: any) {
      alert('Fehler: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const speichereNotiz = async () => {
    setSaving(true);
    try {
      await updateAnfrage(anfrage.id, { praxis_notiz: notiz });
      onChange();
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!confirm('Diese Anforderung endgültig entfernen?')) return;
    try {
      await deleteAnfrage(anfrage.id);
      onChange();
    } catch (e: any) {
      alert('Fehler: ' + e.message);
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>{anfrage.patient.vorname} {anfrage.patient.nachname}</h2>
          <div style={{ color: '#5F6B68', fontSize: 13, marginTop: 4 }}>
            Empfangen: {new Date(anfrage.empfangen_am).toLocaleString('de-DE')}
          </div>
        </div>
        <span className={`status-badge status-${anfrage.status}`}>{STATUS_LABEL[anfrage.status]}</span>
      </div>

      <Section title="Patient">
        <KV k="Name" v={`${anfrage.patient.vorname} ${anfrage.patient.nachname}`} />
        {anfrage.patient.geburtsdatum && <KV k="Geburtsdatum" v={anfrage.patient.geburtsdatum} />}
        {anfrage.patient.versicherten_nr && <KV k="Versicherten-Nr." v={anfrage.patient.versicherten_nr} />}
      </Section>

      {(() => {
        const typ = (anfrage.typ ?? 'krankmeldung') as AnfrageTyp;
        if (typ === 'folgerezept' && anfrage.folgerezept) {
          return (
            <Section title="Folgerezept">
              <KV k="Medikament" v={anfrage.folgerezept.medikament} />
              {anfrage.folgerezept.staerke && <KV k="Stärke / Dosis" v={anfrage.folgerezept.staerke} />}
              {anfrage.folgerezept.anwendung && <KV k="Anwendung" v={anfrage.folgerezept.anwendung} />}
              {anfrage.folgerezept.bestehende_verordnung && (
                <KV k="Hinweis vom Patienten" v={anfrage.folgerezept.bestehende_verordnung} />
              )}
            </Section>
          );
        }
        if (typ === 'ueberweisung' && anfrage.ueberweisung) {
          return (
            <Section title="Überweisung">
              <KV k="Fachrichtung" v={anfrage.ueberweisung.fachrichtung} />
              <KV k="Grund / Beschwerden" v={anfrage.ueberweisung.grund} />
              {anfrage.ueberweisung.wunsch_praxis && <KV k="Wunsch-Praxis" v={anfrage.ueberweisung.wunsch_praxis} />}
              {anfrage.ueberweisung.dringlichkeit === 'eilig' && (
                <KV k="Dringlichkeit" v={<strong style={{ color: '#D9534F' }}>EILIG</strong>} />
              )}
            </Section>
          );
        }
        return (
          <Section title="Krankmeldung">
            <KV k="Beschwerde" v={anfrage.beschwerde} />
            <KV k="Zeitraum" v={`${anfrage.zeitraum.von} – ${anfrage.zeitraum.bis}`} />
            {anfrage.hinweis && <KV k="Hinweis" v={anfrage.hinweis} />}
          </Section>
        );
      })()}

      <Section title="Absender">
        <KV k="Name" v={anfrage.anfordernder.name} />
        <KV k="Verhältnis" v={ROLLE_LABEL[anfrage.anfordernder.rolle] ?? anfrage.anfordernder.rolle} />
        <KV k="E-Mail" v={<a href={`mailto:${anfrage.anfordernder.email}?subject=Krankmeldung%20-%20${encodeURIComponent(anfrage.patient.vorname + ' ' + anfrage.patient.nachname)}`}>{anfrage.anfordernder.email}</a>} />
      </Section>

      <Section title="Praxis-Notiz (intern)">
        <textarea
          rows={3}
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          placeholder="z. B. Krankmeldung wurde per Post versandt"
          style={{ width: '100%', padding: 10, fontSize: 13, border: '1px solid #DDE9E8', borderRadius: 6, fontFamily: 'inherit' }}
        />
        <button className="btn btn-ghost" style={{ marginTop: 6, fontSize: 12 }} onClick={speichereNotiz} disabled={saving}>
          Notiz speichern
        </button>
      </Section>

      <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {anfrage.status !== 'bestaetigt' && (
          <button className="btn btn-success" disabled={saving} onClick={() => openStatusModal('bestaetigt')}>
            ✓ Bestätigen
          </button>
        )}
        {anfrage.status !== 'abgelehnt' && (
          <button className="btn btn-danger" disabled={saving} onClick={() => openStatusModal('abgelehnt')}>
            ✕ Ablehnen
          </button>
        )}
        {anfrage.status !== 'erledigt' && (
          <button className="btn btn-ghost" disabled={saving} onClick={() => openStatusModal('erledigt')}>
            Als erledigt markieren
          </button>
        )}
        {anfrage.status !== 'eingegangen' && (
          <button className="btn btn-ghost" disabled={saving} onClick={() => openStatusModal('eingegangen')}>
            ↺ Zurücksetzen
          </button>
        )}
        <button className="btn btn-ghost" style={{ color: '#D9534F', marginLeft: 'auto' }} onClick={onDelete}>
          🗑 Entfernen
        </button>
      </div>

      {statusModal && (
        <StatusChangeModal
          anfrage={anfrage}
          status={statusModal.status}
          note={statusModal.note}
          onChangeNote={(n) => setStatusModal({ ...statusModal, note: n })}
          onClose={() => setStatusModal(null)}
          onConfirm={confirmStatus}
          saving={saving}
        />
      )}
    </div>
  );
}

function StatusChangeModal({
  anfrage, status, note, onChangeNote, onClose, onConfirm, saving,
}: {
  anfrage: KrankmeldungAnfrage;
  status: 'bestaetigt' | 'abgelehnt' | 'erledigt' | 'eingegangen';
  note: string;
  onChangeNote: (n: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  saving: boolean;
}) {
  const typ = (anfrage.typ ?? 'krankmeldung') as AnfrageTyp;
  const anfrageLabel = TYP_LABEL[typ];
  const statusInfo = {
    bestaetigt: { label: 'bestätigen', color: '#2E7D32', icon: '✓',
      placeholder: typ === 'krankmeldung'
        ? 'z. B. AU wird per Post versendet. Beim Hausarzt-Folgetermin bitte einplanen.'
        : typ === 'folgerezept'
          ? 'z. B. Rezept liegt ab morgen an der Anmeldung bereit. Oder: E-Rezept ist auf Ihre Karte geladen.'
          : 'z. B. Überweisung liegt am Empfang. Bitte schnellstmöglich Termin beim Facharzt vereinbaren.',
    },
    abgelehnt: { label: 'ablehnen', color: '#D9534F', icon: '✕',
      placeholder: typ === 'krankmeldung'
        ? 'z. B. Bitte zu einem Termin in der Praxis kommen, damit wir die Beschwerden persönlich besprechen können.'
        : typ === 'folgerezept'
          ? 'z. B. Dieses Medikament benötigt eine ärztliche Kontrolle. Bitte einen Termin vereinbaren.'
          : 'z. B. Vor der Überweisung ist ein klärendes Gespräch in der Praxis nötig.',
    },
    erledigt: { label: 'als erledigt markieren', color: '#5F6B68', icon: '·',
      placeholder: 'Optionale Nachricht an den Patienten.',
    },
    eingegangen: { label: 'zurücksetzen', color: '#5F6B68', icon: '↺',
      placeholder: 'Hinweis, warum der Status zurückgesetzt wurde (optional, intern).',
    },
  }[status];

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,20,19,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#fff', padding: 28, borderRadius: 12,
          width: 520, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 18, background: statusInfo.color,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 700,
          }}>{statusInfo.icon}</div>
          <h2 style={{ margin: 0, fontSize: 20 }}>
            {anfrageLabel} {statusInfo.label}
          </h2>
        </div>
        <p style={{ color: '#5F6B68', fontSize: 13, margin: '4px 0 18px 0', lineHeight: 1.5 }}>
          Patient/in <strong>{anfrage.patient.vorname} {anfrage.patient.nachname}</strong>. Ihre
          Nachricht wird in der Bertsy-App im Chat-Thread und in der Status-Mail angezeigt — sowie
          intern als Praxis-Notiz gespeichert.
        </p>

        <label style={{ display: 'block', fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
          Nachricht an den Patienten (optional)
        </label>
        <textarea
          rows={5}
          value={note}
          onChange={(e) => onChangeNote(e.target.value)}
          placeholder={statusInfo.placeholder}
          maxLength={500}
          style={{
            width: '100%', padding: 12, fontSize: 14,
            border: '1px solid #DDE9E8', borderRadius: 6,
            fontFamily: 'inherit', resize: 'vertical', minHeight: 100,
          }}
          autoFocus
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <div style={{ fontSize: 11, color: '#5F6B68' }}>
            {note.length} / 500 Zeichen
          </div>
          {note.length === 0 && (
            <div style={{ fontSize: 11, color: '#5F6B68', fontStyle: 'italic' }}>
              Leer lassen = Standard-Text der App wird verwendet
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Abbrechen</button>
          <button
            className="btn"
            style={{ background: statusInfo.color, color: '#fff', fontWeight: 700 }}
            onClick={onConfirm}
            disabled={saving}
          >
            {saving ? 'Wird gesendet…' : `${statusInfo.icon} ${anfrageLabel} ${statusInfo.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 16 }}>
      <h4 className="section-title">{title}</h4>
      {children}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '4px 0' }}>
      <div style={{ color: '#5F6B68', minWidth: 120 }}>{k}:</div>
      <div style={{ flex: 1 }}>{v}</div>
    </div>
  );
}
